import type { ComponentType } from 'react';
import {
    CardDetailsModel,
    HostedCardTokenMetadataTier,
    type Address,
    type CardType,
    type GetDelegateTokenResponse,
    type IpgBrowserTokenizationResult,
    type LosslessNumber,
} from '@pillar-portal/api-types';
import { GatewayId } from '@pillar-portal/database/dist/frontend';
import { CardPointeHostedInvoiceCardEntry } from '@/app/pay/[token]/CardPointeHostedInvoiceCardEntry';
import { CloverHostedInvoiceCardEntry } from '@/app/pay/[token]/CloverHostedInvoiceCardEntry';

export type PortalHostedCardEntryTokenResult = {
    token: string;
    tokenizationResult: IpgBrowserTokenizationResult;
    cardholderName: string;
    expiryDate?: string;
    billingAddress?: Address;
};

export type PortalHostedCardTokenResult = Omit<
    PortalHostedCardEntryTokenResult,
    'tokenizationResult'
> & { tokenizationResult?: IpgBrowserTokenizationResult } & (
        | {
              tokenMetadataTier: typeof HostedCardTokenMetadataTier.METADATA_RICH;
              panPrefix?: string;
              cardType: CardType;
              cardBrand: string;
              cardLast4: string;
              surchargeAmount?: LosslessNumber;
          }
        | {
              tokenMetadataTier: typeof HostedCardTokenMetadataTier.TOKEN_ONLY;
          }
    );

export type PortalHostedCardEntryProps = {
    delegateToken: GetDelegateTokenResponse;
    disabled: boolean;
    onTokenized: (result: PortalHostedCardEntryTokenResult) => void;
    onError: (message: string) => void;
    /**
     * Entries that secure the card only on request (Clover) tokenize each time
     * this increases; Pay Now raises it so one click secures the card and pays.
     * Entries that tokenize as the customer types (CardPointe) ignore it.
     */
    tokenizationRequestId?: number;
    /** Reported by on-request entries once their fields can be tokenized. */
    onTokenizationReadyChange?: (ready: boolean) => void;
};

export type PortalCardEntryAdapter =
    | {
          model: typeof CardDetailsModel.FULLY_OWNED;
      }
    | {
          model: typeof CardDetailsModel.HOSTED_IFRAME | typeof CardDetailsModel.HOSTED_FIELDS;
          component: ComponentType<PortalHostedCardEntryProps> | null;
      };

export type PortalHostedCardEntryAdapter = Extract<
    PortalCardEntryAdapter,
    { component: ComponentType<PortalHostedCardEntryProps> | null }
>;

export function isPortalHostedCardEntryAdapter(
    adapter: PortalCardEntryAdapter | null | undefined
): adapter is PortalHostedCardEntryAdapter {
    return (
        adapter !== null && adapter !== undefined && adapter.model !== CardDetailsModel.FULLY_OWNED
    );
}

const PORTAL_CARD_ENTRY_ADAPTERS: Readonly<
    Record<GatewayId, Partial<Record<CardDetailsModel, PortalCardEntryAdapter>>>
> = {
    [GatewayId.VALOR_US]: {
        [CardDetailsModel.FULLY_OWNED]: {
            model: CardDetailsModel.FULLY_OWNED,
        },
    },
    [GatewayId.CARDPOINTE]: {
        [CardDetailsModel.HOSTED_IFRAME]: {
            model: CardDetailsModel.HOSTED_IFRAME,
            component: CardPointeHostedInvoiceCardEntry,
        },
    },
    [GatewayId.CLOVER]: {
        [CardDetailsModel.HOSTED_FIELDS]: {
            model: CardDetailsModel.HOSTED_FIELDS,
            component: CloverHostedInvoiceCardEntry,
        },
    },
};

export function resolvePortalCardEntryAdapter(
    gatewayId: GatewayId,
    model: CardDetailsModel
): PortalCardEntryAdapter {
    const adapter = PORTAL_CARD_ENTRY_ADAPTERS[gatewayId][model];
    if (adapter === undefined) {
        throw new Error(`Card entry model ${model} is not supported by gateway ${gatewayId}.`);
    }
    return adapter;
}
